import React, { useEffect, useState, useCallback, forwardRef } from "react";
import "devextreme/dist/css/dx.light.css";
import { Button } from "devextreme-react/button";
import TreeView from "devextreme-react/tree-view";
import { LoadIndicator } from "devextreme-react/load-indicator";
import PropTypes from "prop-types";
import { addOrgSvg, orgCollapseSvg } from "../../Assets/SVGs/organizationSVGs";
import { Roles } from "../../Constants/constants";
import { useUser } from "../../contexts/UserContext";
import "./OrganizationTree.scss";

const LoadingTree = () => (
  <div className="search-load">
    <LoadIndicator id="organizationtree-loadindicator-small" height={20} width={20} />
  </div>
);

const OrganizationTree = forwardRef(
  (
    {
      setIsCreateOrgPopupVisibleCallback,
      setSelectedOrgCallback,
      organizationCreated,
      setOrganizationCreated,
      setNodeId,
      nodeId,
    },
    ref
  ) => {
    const { userRoles, backend } = useUser();
    const isSuperUser = userRoles?.includes(Roles.SUPER_USER);
    const isAdminUser = userRoles?.includes(Roles.ORG_ADMIN);
    const [rootOrgData, setRootOrgData] = useState(null);
    const [treeViewCollapse, setTreeViewCollapse] = useState(false);
    const [levelOfNode, setLevelOfNode] = useState("");

    useEffect(() => {
      backend.callGetRootOrg().then((orgData) => {
        setRootOrgData(orgData.data);
      });
    }, [backend, userRoles]);

    const handleSelectOrg = useCallback(
      (e) => {
        setSelectedOrgCallback(e.itemData);
      },
      [setSelectedOrgCallback]
    );

    useEffect(() => {
      if (organizationCreated && nodeId) {
        const treeViewInstance = ref.current.instance;
        backend
          .callAllOrganizationsWithChildPartners(nodeId)
          .then((res) => {
            let isThirdLevel = false;
            let level = "";
            if (levelOfNode === "first") {
              level = "second";
              isThirdLevel = false;
            } else if (levelOfNode === "second") {
              level = "third";
              isThirdLevel = true;
            }
            let items = treeViewInstance.option("items");
            items = items.filter((e) => e.parentId !== nodeId);
            items = items.concat(res?.data);
            items = items.map((item) => {
              if (item.parentId === nodeId) {
                return { ...item, isThirdLevel, level };
              }
              if (item.id === nodeId) {
                return { ...item, hasItems: true };
              }
              return item; // Return the original item if no conditions match
            });
            treeViewInstance.option("items", items);
            treeViewInstance.expandItem(nodeId);
          })
          .finally(() => {
            if (organizationCreated === true && nodeId !== null) {
              setOrganizationCreated(false);
              setNodeId(null);
              setLevelOfNode("");
            }
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationCreated, ref, nodeId]);

    const createChildren = useCallback(
      async (parent) => {
        if (parent) {
          // Check if the parent is a second-level organization (i.e., grandparent is null)
          const isSecondLevel = parent.parent?.parent === null;

          // Check if the parent is a top-level organization (i.e., no parent) for only admin access.
          const isAdminorg = parent?.parent === null;

          // Check if the organization is deeper than the third level (i.e., has a grandparent)
          const isDeeperThanThirdLevel = parent.parent?.parent != null;
          let level = ""; // Initialize level

          if (parent?.parent === null) {
            level = "first";
          } else if (parent.parent?.parent === null) {
            level = "second";
          } else if (parent.parent?.parent != null) {
            level = "third";
          }

          // Create a new object with the level included
          const parentWithLevel = {
            ...parent,
            level,
          };
          const childData = await backend
            .callAllOrganizationsWithChildPartners(parentWithLevel.itemData.id)
            .then((res) => res.data);

          // If it's a top-level organization, the admin user can add organizations
          // (unless they're a super user, in which case they follow different rules).
          if (isAdminorg && isAdminUser && !isSuperUser) {
            // Mark these organizations as third-level, meaning no add button should be shown.
            return childData.map((child) => ({
              ...child,
              isThirdLevel: true,
              level: parentWithLevel.level === "first" ? "second" : "third",
            }));
          }

          // If the parent is a second-level organization or deeper than the third level,
          // also mark them as third-level organizations. (isThirdLevel helps to hide the button)
          if (isSecondLevel || isDeeperThanThirdLevel) {
            return childData.map((child) => ({
              ...child,
              isThirdLevel: true,
              level: parentWithLevel.level === "first" ? "second" : "third",
            }));
          }

          return childData.map((child) => ({
            ...child,
            isThirdLevel: false,
            level: parentWithLevel.level === "first" ? "second" : "third",
          }));
        }
        return rootOrgData;
      },
      [backend, isAdminUser, isSuperUser, rootOrgData]
    );

    const handleToggleCollapse = useCallback(() => {
      setTreeViewCollapse((prevState) => !prevState);
    }, []);

    const handleOpenCreateOrgPopup = useCallback(
      (node) => {
        setIsCreateOrgPopupVisibleCallback(true);
        setNodeId(node?.id);
        if (node?.parentId === null) {
          setLevelOfNode("first");
        } else {
          setLevelOfNode(node?.level);
        }
      },
      [setIsCreateOrgPopupVisibleCallback, setNodeId]
    );

    const renderAddOrgButton = useCallback(
      (node) =>
        (isSuperUser || isAdminUser) && (
          <div>
            <Button
              className="add-org-icon"
              onClick={() => handleOpenCreateOrgPopup(node)}
              stylingMode="text"
            >
              {addOrgSvg}
            </Button>
          </div>
        ),
      [handleOpenCreateOrgPopup, isAdminUser, isSuperUser]
    );

    const renderTreeNode = useCallback(
      (node) => (
        <div className="d-flex ">
          <div className="org-name-number-icon">
            <span title={node.name}>{node.name}</span>
            {!node.isThirdLevel && renderAddOrgButton(node)}
          </div>
        </div>
      ),
      [renderAddOrgButton]
    );

    const renderTree = useCallback(
      () =>
        rootOrgData != null && (
          <div className="organization-tile">
            <TreeView
              ref={ref}
              id="organizationtree-treeview-dynamic"
              keyExpr="id"
              dataStructure="plain"
              rootValue={rootOrgData[0].parentId}
              noDataText="No organizations to display"
              selectionMode="single"
              selectByClick={true}
              hoverStateEnabled={true}
              createChildren={createChildren}
              focusStateEnabled={true}
              onItemSelectionChanged={handleSelectOrg}
              itemRender={renderTreeNode}
              visible={rootOrgData}
            />
          </div>
        ),
      [createChildren, handleSelectOrg, ref, renderTreeNode, rootOrgData]
    );

    return (
      <div
        className={!treeViewCollapse ? "organization-data" : "organization-collapse-data"}
      >
        <Button
          className="collapse-down-button"
          onClick={handleToggleCollapse}
          data-testid="collapse-button"
        >
          {orgCollapseSvg(treeViewCollapse)}
        </Button>
        <div className="organization-data-content">
          <div id="organizationtree-div-tree-wrapper">
            <div className="dx-tile-content">
              {renderTree()}
              {rootOrgData === null && <LoadingTree />}
            </div>
          </div>
        </div>
        {treeViewCollapse && (
          <span className="org-list-collapse-text">Organization List</span>
        )}
      </div>
    );
  }
);

OrganizationTree.propTypes = {
  setIsCreateOrgPopupVisibleCallback: PropTypes.func.isRequired,
  setSelectedOrgCallback: PropTypes.func.isRequired,
  organizationCreated: PropTypes.bool.isRequired,
  setOrganizationCreated: PropTypes.func.isRequired,
  setNodeId: PropTypes.func.isRequired,
  nodeId: PropTypes.number.isRequired,
};
export default OrganizationTree;
